import type { CSSProperties } from 'react';

import goldLogo from '../../assets/Gold colour Logo (1)- copy.png';
import fullLogo from '../../assets/02.png';
import { BRAND_NAME } from '../../lib/brandMark';
import { c } from '../../styles/theme';

const FULL_LOGO_ASPECT_RATIO = 934 / 358;

/** Which of the three forms to draw. */
export type LogoVariant = 'full' | 'mark';

/**
 * How the mark is coloured.
 *
 * `brand` — amber badge, near-black strokes. The default, and what nearly every surface
 *           wants, on dark and on light alike: the badge carries its own background, so it
 *           does not depend on what is behind it. That is the property that makes one mark
 *           work on both themes without a second asset.
 * `mono`   — strokes in the current text colour, no badge. For a surface where the amber
 *           would be wrong or unavailable: a single-colour print, a dense header, a context
 *           that is already amber.
 */
export type LogoTone = 'brand' | 'mono';

export interface LogoProps {
  /** Full lockup (default) or the mark alone. */
  variant?: LogoVariant;
  /** Brand amber (default) or monochrome. */
  tone?: LogoTone;
  /** The badge's edge length in pixels. The wordmark scales from it. */
  size?: number;
  /**
   * Wordmark colour, for the `full` variant.
   *
   * Defaults to `text1`, which is right on every dark surface in the app. A light-theme
   * caller passes its own ink — and this is a prop rather than a `var()` because the
   * component is used inside `document.write`-built print documents' React sibling
   * (PrintHeader) where the app's custom properties are not defined.
   */
  color?: string;
  /**
   * Overrides the accessible name.
   *
   * The default is "Lyrixa AI", which is correct standing alone. A caller that already
   * renders the words beside it — the `full` variant does — must not have them announced
   * twice, so that case is handled internally; this is for a caller with its own context,
   * e.g. "Lyrixa AI home".
   */
  label?: string;
  /**
   * Hides the whole thing from assistive technology.
   *
   * For decorative repetitions: a logo in a print masthead beside a page that already has
   * its own heading, or a mark used as a bullet. PrintHeader passes it.
   */
  decorative?: boolean;
  style?: CSSProperties;
  className?: string;
}

function Mark({
  size,
  title,
}: {
  size: number;
  /** Rendered as an SVG <title> when set; omitted entirely when the mark is decorative. */
  title: string | null;
}) {
  return (
    <img
      src={goldLogo}
      alt={title ?? ''}
      width={size}
      height={size}
      role={title ? undefined : 'presentation'}
      aria-hidden={title ? undefined : true}
      draggable={false}
      style={{ display: 'block', flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

/**
 * The Lyrixa AI logo.
 *
 * `size` is the badge's edge, and the wordmark is derived from it rather than passed
 * separately: the lockup's proportions are part of the logo, and letting a caller set both
 * is how four call sites ended up with three different ones.
 */
export default function Logo({
  variant = 'full',
  size = 26,
  color = c.text1,
  label,
  decorative = false,
  style,
  className,
}: LogoProps) {
  /*
    The mark alone carries the name; in the full lockup the WORDS carry it and the mark is
    decorative, or the logo is announced as "Lyrixa AI Lyrixa AI". Same reasoning as an
    icon inside a labelled button.
  */
  const markTitle = decorative || variant === 'full' ? null : (label ?? BRAND_NAME);

  if (variant === 'mark') {
    return (
      <span
        className={className}
        style={{ display: 'inline-flex', flexShrink: 0, color, ...style }}
      >
        <Mark size={size} title={markTitle} />
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        color,
        ...style,
      }}
      aria-hidden={decorative || undefined}
    >
      {/*
        The previous lockup rendered <Mark size={size} title={markTitle} /> followed by a
        .lyx-logo-wordmark span containing BRAND_NAME. Keep the standalone Mark component
        above for mark-only consumers; 02.png now supplies both pieces of the full lockup.
      */}
      <img
        src={fullLogo}
        alt={decorative ? '' : (label ?? BRAND_NAME)}
        width={Math.round(size * FULL_LOGO_ASPECT_RATIO)}
        height={size}
        role={decorative ? 'presentation' : undefined}
        aria-hidden={decorative || undefined}
        draggable={false}
        style={{
          display: 'block',
          flexShrink: 0,
          objectFit: 'contain',
        }}
      />
    </span>
  );
}
